import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

const JQUERY_NAMESPACE = ".upcomingGifts"
const OPEN_CLASS = "is-open"

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle, this one owns the date-range picker, the automation select filter, and the
// checked gifts plus the bottom bar for canceling them. The cancel itself goes through
// upcoming_gift_cancel_controller.js on the same element, via the "upcoming-gifts:bulk-cancel" event.
export default class extends Controller {
  static get targets() {
    return ["table", "dateRangeInput", "automationFilter", "footer", "selectedCount"];
  }

  static get values() {
    return { begin: String, end: String };
  }

  connect() {
    this.selectedKeys = new Set();

    $(this.dateRangeInputTarget).daterangepicker({
      buttonClasses: "btn btn-main btn-md",
      cancelButtonClasses: "btn-ghost",
      opens: "left",
      alwaysShowCalendars: true,
      showCustomRangeLabel: false,
      ranges: {
        "Today": [moment(), moment()],
        "Yesterday": [moment().subtract(1, "days"), moment().subtract(1, "days")],
        "Last 7 Days": [moment().subtract(6, "days"), moment()],
        "Last 30 Days": [moment().subtract(29, "days"), moment()],
        "This Month": [moment().startOf("month"), moment().endOf("month")],
        "Last Month": [moment().subtract(1, "month").startOf("month"), moment().subtract(1, "month").endOf("month")],
        "All Time": [moment(this.beginValue, "MM-DD-YYYY"), moment(this.endValue, "MM-DD-YYYY")],
      },
    }, (start, end) => this.applyRange(start, end));

    // show.daterangepicker/hide.daterangepicker are announced by the plugin through jQuery's
    // trigger, not a real DOM event, so the binding itself has to stay on jQuery -- but the class
    // toggle it runs is plain DOM work on our own static markup. OPEN_CLASS stays a literal rather
    // than moving to the Classes API: "is-open" is a shared utility class used by several sibling
    // datatable controllers (decisions.md D7 addendum 2).
    $(this.dateRangeInputTarget).
      on(`show.daterangepicker${JQUERY_NAMESPACE}`, () => this.element.querySelector(".date-range-picker").classList.add(OPEN_CLASS)).
      on(`hide.daterangepicker${JQUERY_NAMESPACE}`, () => this.element.querySelector(".date-range-picker").classList.remove(OPEN_CLASS));

    $(this.automationFilterTarget).select2({ placeholder: "Automation", width: "100%" });

    // select2 announces a pick with jQuery's `.trigger("change")`, not a native dispatch, so this
    // has to stay on jQuery's event bus rather than becoming a data-action.
    $(this.element).on(`change${JQUERY_NAMESPACE}`, ".datatable-filter", () => this.reload());

    // draw.dt is announced through jQuery's trigger. Rows are re-rendered on every page change and
    // sort, so re-check the ones that are still selected.
    $(this.tableTarget).on(`draw.dt${JQUERY_NAMESPACE}`, () => this.restoreCheckedRows());

    this.triggerInitialReload();
  }

  disconnect() {
    const picker = $(this.dateRangeInputTarget).data("daterangepicker");
    if (picker) { picker.remove(); }
    $(this.dateRangeInputTarget).off(JQUERY_NAMESPACE);

    $(this.element).off(JQUERY_NAMESPACE);
    $(this.tableTarget).off(JQUERY_NAMESPACE);

    if ($(this.automationFilterTarget).data("select2")) {
      $(this.automationFilterTarget).select2("destroy");
    }
  }

  applyRange(start, end) {
    const newRange = `${start.format("MMDDYYYY")}-${end.format("MMDDYYYY")}`;
    this.reload({ range: newRange });
  }

  // Load-bearing: this is what converts a deep-linked ?automation_id=... (automations/show's
  // "View upcoming gifts" link) into the filter[automation] shape
  // UpcomingGiftDatatable#get_raw_records actually reads - the server-rendered data-source URL
  // carries only automation_id, which the backend never looks at.
  //
  // Sibling controllers' connect() order is not guaranteed, so wait for the `datatable`
  // controller's own init rather than assuming it has already run.
  triggerInitialReload() {
    if ($.fn.DataTable.isDataTable(this.tableTarget)) {
      this.reload();
    } else {
      $(this.tableTarget).one(`init.dt${JQUERY_NAMESPACE}`, () => this.reload());
    }
  }

  toggleSelection(event) {
    const checkbox = event.target;
    if (checkbox.checked) {
      this.selectedKeys.add(checkbox.value);
    } else {
      this.selectedKeys.delete(checkbox.value);
    }
    this.updateFooter();
  }

  clearSelected() {
    this.clearSelection();
  }

  cancelSelected() {
    this.dispatch("bulk-cancel", { detail: { keys: Array.from(this.selectedKeys) } });
  }

  // "upcoming-gift-cancel:canceled", for a single gift or the checked ones
  canceled() {
    this.clearSelection();
    $(this.tableTarget).DataTable().ajax.reload(null, false);
  }

  clearSelection() {
    this.selectedKeys.clear();
    this.tableTarget.querySelectorAll(".upcoming-gift-checkbox").forEach((checkbox) => { checkbox.checked = false; });
    this.updateFooter();
  }

  restoreCheckedRows() {
    this.tableTarget.querySelectorAll(".upcoming-gift-checkbox").forEach((checkbox) => {
      checkbox.checked = this.selectedKeys.has(checkbox.value);
    });
  }

  updateFooter() {
    if (!this.hasFooterTarget) { return; }

    const count = this.selectedKeys.size;
    this.footerTarget.style.display = count > 0 ? "flex" : "none";
    this.selectedCountTarget.textContent = `${count} Upcoming ${count === 1 ? "Gift" : "Gifts"} Selected`;
  }

  // Also runs for every filter and date range change, which start a fresh selection
  reload(extraParams = {}) {
    this.clearSelection();
    ProgressBar.start();

    const filterSelects = this.element.querySelectorAll(".datatable-moveable-filter, .datatable-filter");
    if (filterSelects.length > 0) {
      const filter = {};
      filterSelects.forEach((el) => {
        const key = el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "");
        // select2's own data API, not a plain DOM read -- has to stay jQuery-wrapped.
        filter[key] = ($(el).select2("data") || []).map((option) => option.id);
      });
      extraParams["filter"] = filter;
    }

    this.element.querySelectorAll(".datatable-moveable-text-filter, .datatable-text-filter").forEach((el) => {
      extraParams["filter"][el.getAttribute("name").replace(/(^.*\[|\].*$)/g, "")] = el.value;
    });

    const params = Object.assign(this.currentParams(), extraParams);

    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    this.tableTarget.setAttribute("data-source", replacement);
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Converts a bracket-notation query string into the nested object `$.param()` re-serializes
  // back into the same form. A URLSearchParams rewrite would lose the nesting.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }
}
